import * as React from 'react'
import { Search } from 'lucide-react'

import {
  useCreateRegistrationOrder, useRegistrationOrders, useWatchers,
  type RegistrationBudget,
} from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/misc'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { StatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'

export function toMajor(minor?: number): string {
  return minor == null ? '' : (minor / 100).toFixed(2)
}

function toMinor(major: string): number | undefined {
  const n = Number(major)
  return major.trim() === '' || Number.isNaN(n) ? undefined : Math.round(n * 100)
}

/**
 * 新增注册单。
 *
 * 域名只能从监控列表里挑，不能自由输入。这不是为了少写一个输入框：抢注靠的是监控
 * 发现掉落后立刻唤醒本单，一个不在监控里的域名根本没有东西会通知它，那样的单只能
 * 靠自己反复问注册商——既慢又贵。把选择限制在监控表内，等于让「这张单会不会真的
 * 生效」在建单那一刻就是确定的。
 *
 * 从监控页点进来时域名已经定死（lockedDomain），选择器不再出现。
 */
export function CreateOrderDialog({
  budget, open, onOpenChange, lockedDomain,
}: {
  budget: RegistrationBudget
  open: boolean
  onOpenChange: (o: boolean) => void
  lockedDomain?: string
}) {
  const toast = useToast()
  const create = useCreateRegistrationOrder()
  const [domain, setDomain] = React.useState(lockedDomain ?? '')
  const [years, setYears] = React.useState('1')
  const [maxPrice, setMaxPrice] = React.useState(toMajor(budget.per_order_cap_minor))
  const [maxAttempts, setMaxAttempts] = React.useState('60')
  const [startAt, setStartAt] = React.useState('')
  const [expireAt, setExpireAt] = React.useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    const minor = toMinor(maxPrice)
    if (!minor) {
      toast.error('请填写价格上限')
      return
    }
    if (!domain) {
      toast.error('请从监控列表中选择一个域名')
      return
    }
    try {
      const order = await create.mutateAsync({
        domain_ascii: domain.trim().toLowerCase(),
        years: Number(years) || 1,
        max_price_minor: minor,
        currency: budget.currency,
        max_attempts: Number(maxAttempts) || undefined,
        // datetime-local has no zone; the browser's own offset is the one the
        // person typing meant.
        start_at: startAt ? new Date(startAt).toISOString() : undefined,
        expire_at: expireAt ? new Date(expireAt).toISOString() : undefined,
      })
      toast.success('注册单已建立', order.domain_ascii)
      onOpenChange(false)
    } catch (err) {
      toast.error('建立失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>新增注册单</DialogTitle>
            <DialogDescription>
              域名被确认可注册且报价不超过上限时自动下单。报价高于上限会直接终止本单，
              不会按更高的价格买入。
            </DialogDescription>
          </DialogHeader>

          {lockedDomain ? (
            <Field label="域名">
              <div className="flex h-9 items-center rounded-md border bg-muted/40 px-3 font-mono text-sm">
                {lockedDomain}
              </div>
            </Field>
          ) : (
            <DomainPicker value={domain} onChange={setDomain} />
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label="注册年限">
              <Input type="number" min="1" max="10" value={years} onChange={(e) => setYears(e.target.value)} />
            </Field>
            <Field label={`价格上限（${budget.currency}）`} required>
              <Input type="number" step="0.01" min="0" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} required />
            </Field>
            <Field label="最多下单轮次" hint="每轮同时向所有已启用服务商各发一次">
              <Input type="number" min="1" value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="从何时开始盯" hint="留空＝立即开始">
              <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
            </Field>
            <Field label="盯到何时为止" hint="留空＝不设期限；到期后本单不再尝试">
              <Input type="datetime-local" value={expireAt} onChange={(e) => setExpireAt(e.target.value)} />
            </Field>
          </div>

          <p className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
            掉落由监控发现——它走 RDAP，比反复问注册商快也便宜，域名进入 pendingDelete
            后还会自动切到秒级频率。一旦确认可注册就立刻唤醒本单下单。
            <strong>轮次只在真正向注册商发出注册请求时才 +1</strong>，等待期间不消耗。
            每一轮所有已启用的服务商同时开抢——同一个域名在注册局只有一个，
            并发不会重复购买，只会提高抢到的概率。
          </p>

          <p className="text-xs text-muted-foreground">
            单笔上限为 {budget.currency} {toMajor(budget.per_order_cap_minor)}，
            今日剩余额度 {budget.currency} {toMajor(Math.max(0, budget.daily_cap_minor - budget.spent_today_minor))}。
          </p>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            <Button type="submit" disabled={create.isPending || !domain}>建立注册单</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * 从监控列表里挑一个域名。
 *
 * 已经有在途注册单的域名会被标出并禁选——同一域名同时只允许一张有效单，
 * 与其让服务端在提交后报错，不如在列表上就说清楚。
 */
function DomainPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { data, isLoading } = useWatchers()
  const { data: orderData } = useRegistrationOrders()
  const [query, setQuery] = React.useState('')

  const taken = new Set(
    (orderData?.orders ?? [])
      .filter((o) => ['queued', 'registering', 'verifying'].includes(o.status))
      .map((o) => o.domain_ascii),
  )

  const items = (data?.items ?? []).filter((w) =>
    query.trim() === '' || w.domain.toLowerCase().includes(query.trim().toLowerCase()),
  )

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        域名<span className="text-destructive"> *</span>
        <span className="ml-1 font-normal text-muted-foreground">只能从监控列表中选择</span>
      </Label>

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          className="h-9 pl-8" value={query} placeholder="筛选监控中的域名"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="max-h-52 overflow-auto rounded-md border">
        {isLoading ? (
          <p className="p-4 text-center text-xs text-muted-foreground">加载监控列表…</p>
        ) : items.length === 0 ? (
          <p className="p-4 text-center text-xs text-muted-foreground">
            {data?.items?.length
              ? '没有匹配的域名。'
              : '监控列表是空的。请先在「监控」页把想抢的域名加入监控，注册单只能为受监控的域名建立。'}
          </p>
        ) : (
          <ul className="divide-y">
            {items.map((w) => {
              const disabled = taken.has(w.domain)
              const selected = value === w.domain
              return (
                <li key={w.id}>
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(w.domain)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm',
                      disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted/50',
                      selected && 'bg-primary/10',
                    )}
                  >
                    <span className="truncate font-mono text-xs">{w.domain}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {disabled && <Badge variant="muted">已有注册单</Badge>}
                      <StatusBadge status={w.current_status} />
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export function Field({
  label, hint, required, children,
}: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">
        {label}{required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
      {hint && <p className="text-[0.7rem] text-muted-foreground">{hint}</p>}
    </div>
  )
}
